/**
 * OCGC-3163 — strip the stray "$" that React's streaming-SSR reveal function leaves on prod.
 *
 * React Router streams deferred Suspense boundaries after </html>. The browser parses those
 * bytes in "after after body" mode: the opening <!--$?--> marker is reparented up to the
 * document (a sibling of <html>) while its <template> lands in <body>, separating the two.
 * React's inline reveal function ($RC) then reads template.previousSibling — no longer the
 * comment marker — and assigns `.data = "$"`. On production the node in that position is the
 * trailing whitespace text node left by Cloudflare's injected Web Analytics beacon
 * (`…</script>\n</body>`), so the write surfaces as a visible "$" at the bottom of the page.
 * On local/staging that position is an element, where `.data = "$"` sets an invisible expando,
 * which is why the symptom is prod-only.
 *
 * $RC mutates an existing text node's data rather than inserting a node, so a plain childList
 * observer never fires — characterData must be watched too. The initial pass clears any node
 * already corrupted before hydration; the observer catches boundaries that resolve later, then
 * disconnects once the page has loaded.
 *
 * TODO(OCGC-3163): remove once the Cloudflare beacon auto-injection is owned by the app (or
 * otherwise stops leaving a trailing text node as the last <body> child).
 */

/**
 * Query param that switches the whole workaround off. Append `?disableDollarSignScrubber=true` to
 * the URL (e.g. when validating a React 19 upgrade or another fix) to verify the issue is gone
 * without the scrubber running. Only the explicit value "true" disables it — any other value (or
 * an absent param) leaves the scrubber running, so the behaviour is unambiguous.
 */
export const DISABLE_QUERY_PARAM = 'disableDollarSignScrubber';

const isScrubberDisabled = (): boolean =>
  new URLSearchParams(window.location.search).get(DISABLE_QUERY_PARAM) === 'true';

/**
 * Remove any direct <body> child text node whose trimmed value is exactly "$".
 *
 * Only text nodes are targeted: the prod corruption is always a text node (the trailing
 * whitespace node $RC overwrites). Comment nodes are deliberately left alone — a *resolved*
 * React Suspense boundary's opening marker is `<!--$-->` (comment data exactly "$"), so
 * scrubbing comment "$" nodes would risk deleting a legitimate boundary marker. Only direct
 * children are scanned, so legitimate "$" text deeper in the tree is untouched.
 */
export const scrubDollarSignNodes = (): void => {
  for (const node of Array.from(document.body.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim() === '$') {
      node.parentNode?.removeChild(node);
    }
  }
};

/**
 * Run the initial scrub, then watch <body> for boundaries that resolve after hydration
 * ($RC mutates an existing text node's data, so characterData is observed, not just childList).
 * The observer disconnects once the page has loaded, bounding it to the streaming window.
 *
 * entry.client.tsx awaits a dynamic Sentry import (and MSW setup) before calling this, which
 * can resolve after the `load` event has already fired. In that case streaming is finished, a
 * `load` listener would never run, and the one-shot scrub above already covers every resolved
 * boundary — so we skip the observer entirely rather than leak it for the page's lifetime.
 *
 * No-op when the `?disableDollarSignScrubber` query param is present, so the workaround can be
 * switched off per request without a deploy.
 */
export const installDollarSignScrubber = (): void => {
  if (isScrubberDisabled()) {
    return;
  }

  scrubDollarSignNodes();

  if (document.readyState === 'complete') {
    return;
  }

  const observer = new MutationObserver(scrubDollarSignNodes);
  observer.observe(document.body, { characterData: true, childList: true, subtree: true });
  window.addEventListener('load', () => observer.disconnect(), { once: true });
};
